// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import Draggable from "react-draggable";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";

// components
import {
  CalendarDayAugmentedEvent,
  CalendarDayEventTooltipOptions,
  CalendarWeek,
} from "@clockwise/web-commons/src/components/calendar";
import { Event } from "@components/event";

// web-app-calendar-view imports
import { IContainer, IProps, IState } from "./WebAppCalendar.d";
import { webAppCalendarFragments } from "./WebAppCalendar.gql";
import { styles } from "./WebAppCalendar.styles";

// state
import { setActiveViewEvent } from "@actions/chrome-extension.actions";
import { setScrollIsLatched, setScrollOffset } from "@actions/day-view.actions";
import { setCurrentTip, WeekViewCurrentTip } from "@actions/week-view.actions";
import { IReduxState } from "@reducers/root.reducer";

// util
import {
  isLunchSmartHold,
  isSmartHold,
  isTeamCalendarSyncEvent,
} from "@clockwise/client-commons/src/util/event";
import { isAutopilotExplicitlyEnabled } from "@clockwise/client-commons/src/util/event-tag";
import { isOnCalendarEventModal } from "@util/experiment.util";
import { getNextWeekViewTip, updateWeekViewFlags } from "@util/flags.util";
import { fromGlobalId } from "@util/graphql.util";
import { ZonedMoment } from "@util/time-zone.util";

// constants
import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";
import { daysOfWeek } from "@constants/date.constant";

// material-ui
import { Dialog, IconButton, Paper } from "@material-ui/core";
import { PaperProps } from "@material-ui/core/Paper";
import withStyles from "@material-ui/core/styles/withStyles";
import { Close } from "@material-ui/icons";

function PaperComponent(props: PaperProps) {
  return (
    <Draggable bounds="parent">
      <Paper {...props} />
    </Draggable>
  );
}

///////////////
// Component //
///////////////
class WebAppCalendarCmpt extends React.Component<IProps, IState> {
  private scrollRef: React.RefObject<HTMLDivElement>;
  private ignoreNextScroll: boolean = false;
  private hasInitialized: boolean = false;
  private hasInitializedOffset: boolean = false;
  private minuteInterval?: ReturnType<typeof setInterval>;
  private expectedStepTime: ZonedMoment;

  constructor(props: IProps) {
    super(props);

    this.state = {
      selectedEvent: null,
    };

    this.scrollRef = React.createRef();
  }

  private onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (this.ignoreNextScroll) {
      this.ignoreNextScroll = false;
      return;
    }

    this.props.setScrollOffset(event.currentTarget.scrollTop);
    this.props.setScrollIsLatched(false);
  };

  private onSelectEvent = (calendarId: string, externalEventId: string) => {
    if (isOnCalendarEventModal()) {
      this.setState({
        selectedEvent: {
          calendarId,
          externalEventId,
        },
      });
    } else {
      this.props.setActiveViewEvent(externalEventId, calendarId, false);
    }
  };

  private onMinuteChange = () => {
    const now = new ZonedMoment();
    let dt = now.valueOf() - this.expectedStepTime.valueOf();

    if (dt > 60000) {
      dt = dt % 60000;
    }

    this.updateScrollPosition();

    this.minuteInterval = setTimeout(() => this.onMinuteChange(), 60000 - dt);
  };

  private onDismissTip = () => {
    const { currentTip, viewer } = this.props;

    const flagsErrorable = viewer?.user?.webAppFlagsErrorable;

    if (
      currentTip === WeekViewCurrentTip.Autopilot ||
      currentTip === WeekViewCurrentTip.TeamEvent
    ) {
      updateWeekViewFlags(this.props.relay.environment, currentTip).finally(() =>
        this.props.setCurrentTip(getNextWeekViewTip(flagsErrorable, currentTip)),
      );
    }
  };

  private onMoreTip = () => {
    const { currentTip } = this.props;

    if (currentTip === WeekViewCurrentTip.TeamEvent) {
      window.open(articleUrls.teamCalendar, "_blank");
    }
  };

  private updateScrollPosition(maybeTimeOffset?: ZonedMoment) {
    if (this.scrollRef && this.scrollRef.current) {
      const { current } = this.scrollRef;
      if (this.props.scrollIsLatched && !maybeTimeOffset) {
        const now = new ZonedMoment();
        const timeOffset = (now.hours() + now.minutes() / 60) / 23;
        const scrollTop = current.scrollHeight * timeOffset - current.offsetHeight / 3;
        current.scrollTop = scrollTop;

        if (current.scrollHeight && !this.hasInitializedOffset) {
          this.props.setScrollOffset(scrollTop);
          this.hasInitializedOffset = true;
        }
      } else if (maybeTimeOffset) {
        const timeOffset = (maybeTimeOffset.hours() + maybeTimeOffset.minutes() / 60) / 23;
        const scrollTop = current.scrollHeight * timeOffset - current.offsetHeight / 3;
        current.scrollTop = scrollTop;

        this.props.setScrollOffset(scrollTop);
        this.props.setScrollIsLatched(false);
      } else {
        current.scrollTop = this.props.scrollOffset;
      }
      this.ignoreNextScroll = true;
    }
  }

  private maybeSetTip(props: IProps) {
    const { viewer } = props;

    const flagsErrorable = viewer?.user?.webAppFlagsErrorable;

    if (flagsErrorable && !this.hasInitialized) {
      let nextTip = getNextWeekViewTip(flagsErrorable, WeekViewCurrentTip.None);

      // override next tip with param if its determined to be None
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("teams-migration")) {
        urlParams.delete("teams-migration");
        let newSearch = urlParams.toString();
        newSearch = newSearch === "?" ? "" : newSearch;
        const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${newSearch}${window.location.hash}`;
        window.history.pushState({ path: newUrl }, "", newUrl);
        if (nextTip === WeekViewCurrentTip.None) {
          nextTip = WeekViewCurrentTip.TeamEvent;
        }
      }

      this.props.setCurrentTip(nextTip);

      this.hasInitialized = true;
    }
  }

  public UNSAFE_componentWillMount() {
    const now = new ZonedMoment();
    const driftTime = 60000 - now.seconds() * 1000 + now.milliseconds();
    this.expectedStepTime = now.add(driftTime, "milliseconds");

    this.minuteInterval = setTimeout(() => this.onMinuteChange(), driftTime);

    this.maybeSetTip(this.props);
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const { org, viewer } = nextProps;

    const flagsErrorable = viewer?.user?.webAppFlagsErrorable;

    this.maybeSetTip(nextProps);

    if (this.props.currentTip !== nextProps.currentTip) {
      const epbwbw =
        org &&
        org.eventParentBaseWrapperByWeeksObject.__typename !== "GraphEntityError" &&
        org.eventParentBaseWrapperByWeeksObject.list[0];

      const eventParentBases = (epbwbw && epbwbw.eventParentBases) || [];

      const eventParentBaseMap: { [key: string]: number } = {};

      eventParentBases.forEach(
        (eventParentBase) =>
          (eventParentBaseMap[fromGlobalId(eventParentBase.id).id] = eventParentBase.timestamp),
      );

      const allEvents =
        (epbwbw &&
          epbwbw.eventParentsByDay
            .map((epbd) =>
              epbd.eventParents
                .filter(
                  (ep) => ep.eventParentBaseTimestamp >= eventParentBaseMap[ep.eventParentBaseId],
                )
                .reduce((prev, curr) => prev.concat(curr.events), [] as ISchema.IEvent[]),
            )
            .reduce((prev, events) => [...prev, ...events], [])
            .filter((event) => {
              const youAttendee = event.attendees.find(
                (attendee) => viewer?.user.emails.indexOf(attendee.urnValue) !== -1,
              );

              return (
                !youAttendee || youAttendee.responseStatus !== ISchema.ResponseStatusEnum.Declined
              );
            })
            .sort((a, b) => {
              const aMoment = new ZonedMoment(a.startTime.millis);
              const bMoment = new ZonedMoment(b.startTime.millis);

              const aNoonOffset = Math.abs(aMoment.hour() - 12);
              const bNoonOffset = Math.abs(bMoment.hour() - 12);

              const aLunchHold = isLunchSmartHold(a);
              const bLunchHold = isLunchSmartHold(b);

              if (aLunchHold && !bLunchHold) {
                // deprioritize lunch holds
                return 1;
              } else if (!aLunchHold && bLunchHold) {
                return -1;
              } else if (aNoonOffset < bNoonOffset) {
                // then prioritize by middle of day
                return -1;
              } else if (aNoonOffset > bNoonOffset) {
                return 1;
              }

              return 0;
            })) ||
        [];

      const currentTip = nextProps.currentTip;
      let tooltipEvent: ISchema.IEvent | undefined = undefined;
      let shouldProgress = false;

      let header = "";
      let body: string | undefined = undefined;
      let dismissText: string | undefined = undefined;
      let moreText: string | undefined = undefined;
      let onMore: (() => void) | undefined = undefined;

      if (currentTip === WeekViewCurrentTip.Autopilot) {
        tooltipEvent = allEvents.find((e) => isAutopilotExplicitlyEnabled(e));
        header = "See your flexible meetings at a glance";
        body = `Flexible meetings are labelled with an ❇️ emoji so you know they might move.`;
        dismissText = "Got it";
        shouldProgress = true;
      } else if (currentTip === WeekViewCurrentTip.TeamEvent) {
        tooltipEvent = allEvents.find((e) => isTeamCalendarSyncEvent(e));
        header = `New! Automatic team calendar`;
        body = `Clockwise now syncs your OOO and WFH events to a team calendar, no manual updating required.`;
        dismissText = "Got it";
        moreText = "Learn more";
        onMore = this.onMoreTip;
        shouldProgress = true;
      } else if (currentTip === WeekViewCurrentTip.EventDetails) {
        tooltipEvent = allEvents.find((e) => !isSmartHold(e));
        header = "Try clicking on an event to manage it in Clockwise.";
        shouldProgress = true;
      }

      if (!tooltipEvent && shouldProgress) {
        this.props.setCurrentTip(getNextWeekViewTip(flagsErrorable, currentTip));
      } else if (tooltipEvent && !tooltipEvent.isAllDay) {
        const timeOffset = new ZonedMoment(tooltipEvent.startTime.millis);
        this.updateScrollPosition(timeOffset);
      }

      if (!tooltipEvent) {
        this.setState({ tooltipEvent, tooltipOptions: undefined });
        return;
      }

      const tooltipOptions: CalendarDayEventTooltipOptions = {
        header,
        body,
        dismissText,
        onMore,
        moreText,
        onDismiss: this.onDismissTip,
        tipColor: "white",
      };

      this.setState({ tooltipEvent: tooltipEvent.eventKey.externalEventId, tooltipOptions });
    }
  }

  public componentWillUnmount() {
    if (this.minuteInterval) {
      clearInterval(this.minuteInterval);
      delete this.minuteInterval;
    }
  }

  public componentDidUpdate() {
    this.updateScrollPosition();
  }

  public componentDidMount() {
    this.updateScrollPosition();
  }

  public render() {
    const {
      org,
      rendererOrg,
      userId,
      calendarIds,
      selectedWeekMoment,
      mode,
      showWorkingHours,
      showFragmentedTime,
      viewer,
    } = this.props;

    const epbwbw =
      org &&
      org.eventParentBaseWrapperByWeeksObject.__typename !== "GraphEntityError" &&
      org.eventParentBaseWrapperByWeeksObject.list?.length &&
      org.eventParentBaseWrapperByWeeksObject.list[0];

    const eventParentBases = (epbwbw && epbwbw.eventParentBases) || [];

    const eventParentBaseMap: { [key: string]: number } = {};

    eventParentBases.forEach(
      (eventParentBase) =>
        (eventParentBaseMap[fromGlobalId(eventParentBase.id).id] = eventParentBase.timestamp),
    );

    let allDayEventsMaxCount = 0;

    let eventsByDay =
      (epbwbw &&
        epbwbw.eventParentsByDay.map((epbd, i) => {
          const day = selectedWeekMoment.clone().startOf("week").add(i, "days");

          const events = epbd.eventParents
            .filter((ep) => ep.eventParentBaseTimestamp >= eventParentBaseMap[ep.eventParentBaseId])
            .reduce((prev, curr) => prev.concat(curr.events), [] as ISchema.IEvent[]);

          const normalEvents: CalendarDayAugmentedEvent[] = [];
          const allDayEvents: CalendarDayAugmentedEvent[] = [];

          events.forEach((event) => {
            const youAttendee = event.attendees.find(
              (attendee) => viewer?.user.emails.indexOf(attendee.urnValue) !== -1,
            );

            if (youAttendee && youAttendee.responseStatus === ISchema.ResponseStatusEnum.Declined) {
              return;
            }

            // TODO: https://app.asana.com/0/357266512179263/1202484107231117/f
            let aTooltipAdded = false; // Ensure a tooltip is added to just one instance of one event.

            const FullDayInMillis = 1000 * 60 * 60 * 24; // To emulate Google Calendar, render multi day events as all day events.
            if (
              event.isAllDay ||
              event.endTime.millis - event.startTime.millis >= FullDayInMillis
            ) {
              // All day event endTime is either the day it ends, or sometimes the next day at 00-00-00 hours/minutes/seconds. So,
              // subtract 1 second from the end day to check if the end date falls on 'day', which is the calendar day being rendered.
              const inRange = day.isBetween(
                new ZonedMoment(event.startTime.dateOrDateTime),
                new ZonedMoment(event.endTime.dateOrDateTime).subtract(1, "s"),
                "d",
                "[]",
              );

              const sameDayOOOEvent =
                event.startTime.dateOrDateTime === event.endTime.dateOrDateTime &&
                new ZonedMoment(event.startTime.dateOrDateTime).isSame(day, "d");

              if (inRange || sameDayOOOEvent) {
                let tooltipOptions;
                if (!aTooltipAdded && this.state.tooltipEvent === event.eventKey.externalEventId) {
                  tooltipOptions = this.state.tooltipOptions;
                  aTooltipAdded = true;
                }

                allDayEvents.push({
                  event: Object.assign({}, event, { isAllDay: true }),
                  tooltipOptions,
                });
              }
            } else {
              let tooltipOptions;
              if (!aTooltipAdded && this.state.tooltipEvent === event.eventKey.externalEventId) {
                tooltipOptions = this.state.tooltipOptions;
                aTooltipAdded = true;
              }

              normalEvents.push({
                event,
                tooltipOptions,
              });
            }
          });

          allDayEventsMaxCount = Math.max(allDayEventsMaxCount, allDayEvents.length);

          return {
            day,
            normalEvents,
            allDayEventsMaxCount,
            allDayEvents: allDayEvents.sort((a, b) => {
              if (a.event.eventKey.externalEventId === this.state.tooltipEvent) {
                return -1;
              } else if (b.event.eventKey.externalEventId === this.state.tooltipEvent) {
                return 1;
              }

              return 0;
            }),
          };
        })) ||
      new Array(7).fill(null).map((_n, i) => ({
        day: selectedWeekMoment.clone().startOf("week").add(i, "days"),
        normalEvents: [] as CalendarDayAugmentedEvent[],
        allDayEvents: [] as CalendarDayAugmentedEvent[],
        allDayEventsMaxCount: 0,
      }));

    eventsByDay.forEach((ebd) => (ebd.allDayEventsMaxCount = allDayEventsMaxCount));

    if (mode === "day") {
      const currentIndex = eventsByDay.findIndex(({ day }) =>
        selectedWeekMoment.isSame(day, "days"),
      );
      eventsByDay = eventsByDay.slice(currentIndex, currentIndex + 1);
    }

    const colorSettings =
      (rendererOrg &&
        rendererOrg.eventColoringSettingsErrorable.__typename !== "GraphEntityError" &&
        rendererOrg.eventColoringSettingsErrorable) ||
      undefined;

    if (!eventsByDay) {
      return null;
    }

    const daySettings: ISchema.IDaySetting[] = new Array(7).fill(null);

    org &&
      org.userWorkingHours.workingHours.daySettings.forEach(
        (ds) => (daySettings[daysOfWeek.indexOf(ds.day)] = ds),
      );

    return (
      <React.Fragment>
        <div className={this.props.classes.calendarContainerWrapper}>
          <div
            ref={this.scrollRef}
            onScroll={this.onScroll}
            className={this.props.classes.calendarContainer}
          >
            <CalendarWeek
              onClickDay={this.props.onSelectDay}
              colorSettings={colorSettings}
              onClick={this.onSelectEvent}
              userId={userId}
              calendarIds={calendarIds}
              eventsByDay={eventsByDay}
              daySettings={daySettings as any}
              showWorkingHours={showWorkingHours}
              showFragmentedTime={showFragmentedTime}
            />
          </div>
        </div>
        {this.state.selectedEvent ? (
          <Dialog
            PaperComponent={PaperComponent}
            open={true}
            hideBackdrop={true}
            onClose={() => this.setState({ selectedEvent: null })}
          >
            <div className={this.props.classes.eventContainer}>
              <div className="relative-view">
                <IconButton
                  className="icon-button"
                  onClick={() => this.setState({ selectedEvent: null })}
                >
                  <Close />
                </IconButton>
                <Event
                  externalEventId={this.state.selectedEvent!.externalEventId}
                  calendarId={this.state.selectedEvent!.calendarId}
                  org={rendererOrg}
                  environment={this.props.relay.environment}
                />
              </div>
            </div>
          </Dialog>
        ) : null}
      </React.Fragment>
    );
  }
}

/////////////////
// With Styles //
/////////////////
export const WebAppCalendarStyled = withStyles(styles)(WebAppCalendarCmpt);

///////////
// Redux //
///////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    activeView: state.webExtension.activeView,
    scrollOffset: state.dayView.scrollOffset,
    scrollIsLatched: state.dayView.scrollIsLatched,
    currentTip: state.weekView.currentTip,
  };
}

const WebAppCalendarRedux = connect(mapStateToProps, {
  setScrollOffset,
  setScrollIsLatched,
  setActiveViewEvent,
  setCurrentTip,
})(WebAppCalendarStyled);

///////////
// Relay //
///////////
export const WebAppCalendar = createFragmentContainer<IContainer>(
  WebAppCalendarRedux,
  webAppCalendarFragments,
);
